'use client';

import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { FC } from 'react';
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ReferenceArea,
  XAxis,
  YAxis,
} from 'recharts';
import { PARAMETER_COLORS, parameterStatusUtils } from '../types/ParameterData';

interface Props {
  normalMarkers: number;
  warningMarkers: number;
  criticalMarkers: number;
  outdatedMarkers: number;
}

const chartConfig = {
  desktop: {
    label: 'Desktop',
    color: 'hsl(var(--chart-1))',
  },
} satisfies ChartConfig;

export const OverviewChart: FC<Props> = ({
  normalMarkers,
  warningMarkers,
  criticalMarkers,
  outdatedMarkers,
}) => {
  const chartData = [
    {
      index: 0,
      data: parameterStatusUtils.getGenericLabelForSimpleStatus('normal'),
      value: normalMarkers,
      color: PARAMETER_COLORS.normal,
    },
    {
      index: 1,
      data: parameterStatusUtils.getGenericLabelForSimpleStatus('warning'),
      value: warningMarkers,
      color: PARAMETER_COLORS.warning,
    },
    {
      index: 2,
      data: parameterStatusUtils.getGenericLabelForSimpleStatus('critical'),
      value: criticalMarkers,
      color: PARAMETER_COLORS.critical,
    },
    {
      index: 3,
      data: parameterStatusUtils.getGenericLabelForSimpleStatus('outdated'),
      value: outdatedMarkers,
      color: PARAMETER_COLORS.outdated,
    },
  ];

  const maxValue = Math.max(...chartData.map((item) => item.value));

  const yDomainMax = maxValue * 1.6;

  const CustomXAxisTick = ({
    x,
    y,
    payload,
  }: {
    x: number;
    y: number;
    payload: any;
  }) => (
    <text x={x} y={y + 10} textAnchor='middle'>
      <tspan className='text-black' fill='black'>
        {payload.value}
      </tspan>
    </text>
  );

  // TODO [TM]: There's a shadow on bg, remove it
  // TODO [TM]: overview chart too high
  return (
    <ChartContainer config={chartConfig}>
      <BarChart
        accessibilityLayer
        data={chartData}
        margin={{ top: 20, bottom: 20 }}
      >
        <ReferenceArea
          x1={chartData[0].data}
          x2={chartData[2].data}
          fill='var(--updated-parameters-reference-area)'
          radius={5}
        />
        <ReferenceArea
          x1={chartData[3].data}
          x2={chartData[3].data}
          fill='var(--background-light)'
          radius={5}
        />

        <XAxis
          dataKey='data'
          type='category'
          tickLine={false}
          tickMargin={10}
          axisLine={false}
          tick={CustomXAxisTick}
        />

        <YAxis domain={[0, yDomainMax]} hide={true} />

        <ChartTooltip
          cursor={false}
          content={<ChartTooltipContent hideLabel />}
        />

        <Bar dataKey='value' radius={3}>
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
          <LabelList
            position='top'
            offset={12}
            className='fill-foreground'
            fontSize={18}
            fontWeight='semi-bold'
          />
        </Bar>
      </BarChart>
    </ChartContainer>
  );
};
